import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Activity } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UseFormReturn } from 'react-hook-form'

export interface AutoscalingFormData {
  enabledScale: boolean
  pollingInterval: string | number
  cooldownPeriod: string | number
  minReplicaCount: string | number
  maxReplicaCount: string | number
}

type AutoscalingConfigFormProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  isEnabled: boolean
  className?: string
  compact?: boolean
  showToggle?: boolean
}

export const AutoscalingConfigForm = ({
  form,
  isEnabled,
  className,
  compact = false,
  showToggle = true,
}: AutoscalingConfigFormProps) => {
  return (
    <div
      className={cn(
        'space-y-2 rounded-lg p-3 border transition-all',
        !compact &&
          'min-h-[90px] bg-gradient-to-br from-muted/90 to-muted/40 border-border/40',
        compact && 'bg-card border-border',
        className
      )}
      data-testid="autoscaling-config-form"
    >
      <div className="flex items-center justify-between border-b border-border/40 pb-2">
        <div className="flex items-center gap-2">
          <div className="rounded-md p-1 bg-muted/90">
            <Activity className="h-5 w-5" />
          </div>
          <span className="text-sm font-semibold">Autoscaling</span>
        </div>
      </div>

      {showToggle ? (
        <FormField
          control={form.control}
          name="enabledScale"
          render={({ field }) => (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <FormLabel className="text-xs text-muted-foreground m-0">
                  Status
                </FormLabel>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">
                    {field.value ? 'Enabled' : 'Disabled'}
                  </span>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="autoscaling-switch"
                      className="data-[state=checked]:bg-primary"
                    />
                  </FormControl>
                </div>
              </div>

              {isEnabled && (
                <div className="space-y-3 pt-2 border-t border-border/40">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="pollingInterval"
                      render={({ field: pollingField }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">
                            Polling Interval (seconds)
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="30"
                              {...pollingField}
                              className="h-7 text-xs"
                              data-testid="polling-interval-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cooldownPeriod"
                      render={({ field: cooldownField }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">
                            Cooldown Period (seconds)
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="300"
                              {...cooldownField}
                              className="h-7 text-xs"
                              data-testid="cooldown-period-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="minReplicaCount"
                      render={({ field: minField }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">
                            Min Replicas
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="1"
                              {...minField}
                              className="h-7 text-xs"
                              data-testid="min-replicas-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="maxReplicaCount"
                      render={({ field: maxField }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">
                            Max Replicas
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="5"
                              {...maxField}
                              className="h-7 text-xs"
                              data-testid="max-replicas-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        />
      ) : (
        // Configuration fields only - no toggle
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="pollingInterval"
              render={({ field: pollingField }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">
                    Polling Interval (seconds)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="30"
                      {...pollingField}
                      className="h-7 text-xs"
                      data-testid="polling-interval-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cooldownPeriod"
              render={({ field: cooldownField }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">
                    Cooldown Period (seconds)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="300"
                      {...cooldownField}
                      className="h-7 text-xs"
                      data-testid="cooldown-period-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="minReplicaCount"
              render={({ field: minField }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">
                    Min Replicas
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="1"
                      {...minField}
                      className="h-7 text-xs"
                      data-testid="min-replicas-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxReplicaCount"
              render={({ field: maxField }) => (
                <FormItem>
                  <FormLabel className="text-xs text-muted-foreground">
                    Max Replicas
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="5"
                      {...maxField}
                      className="h-7 text-xs"
                      data-testid="max-replicas-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}
